import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';

const PartLabelPrintDialog = ({ open, onClose, part }) => {
  // Handle print
  const handlePrint = () => {
    if (!part) {
      return;
    }

    // Create print content
    const printWindow = window.open('', '_blank');
    const printContent = generatePrintContent(part);

    printWindow.document.write(printContent);
    printWindow.document.close();

    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);

    // Close dialog after printing
    onClose();
  };

  // Generate print content for 4x6" label with single part barcode
  const generatePrintContent = (partData) => {
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Part Label</title>
          <style>
            @page {
              size: 4in 6in;
              margin: 0.1in;
            }

            body {
              margin: 0;
              padding: 0;
              font-family: Arial, sans-serif;
            }

            .label-page {
              width: 3.8in;
              height: 5.8in;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              border: 1px dashed #ccc;
              padding: 0.2in;
              box-sizing: border-box;
            }

            .barcode-container {
              text-align: center;
              margin-bottom: 0.3in;
              width: 100%;
            }

            .barcode-container canvas {
              max-width: 3.4in;
              height: auto;
            }

            .part-description {
              font-size: 14px;
              font-weight: bold;
              text-align: center;
              word-wrap: break-word;
              line-height: 1.2;
              max-width: 3.4in;
            }

            @media print {
              .label-page {
                border: none;
              }
            }
          </style>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
        </head>
        <body>
          <div class="label-page">
            <div class="barcode-container">
              <canvas id="part-barcode"></canvas>
            </div>
            <div class="part-description">
              ${partData.description}
            </div>
          </div>
          <script>
            window.onload = function() {
              JsBarcode("#part-barcode", "${partData.internal_part_number}", {
                format: "CODE128",
                width: 2,
                height: 80,
                displayValue: true,
                fontSize: 14,
                margin: 10
              });
            };
          </script>
        </body>
      </html>
    `;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PrintIcon sx={{ mr: 1 }} />
          Print Part Label
        </Box>
      </DialogTitle>

      <DialogContent>
        {part ? (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Would you like to print a label for this special order part?
            </Typography>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="primary">
                Part Number: {part.internal_part_number}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Description: {part.description}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              This will print a 4x6" label with barcode and description.
            </Typography>
          </Box>
        ) : (
          <Alert severity="error">
            No part data available for printing.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Skip
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={!part}
        >
          Print Label
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartLabelPrintDialog;
